import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { Dispatch, SetStateAction, useContext, useEffect } from "react";
import { CustomEvent, Map } from "schemas/functions";

import StyledTextField from "components/Styled/TextField";
import { hasSocialMediaProfile } from "utils/influencer";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "../styles";
import {
  SocialMediaFieldKey,
  SocialMediaFields,
  SocialMediaLinks,
  convertOnboardingFormToLink,
  isValidUsername,
  sanitizeInput,
  stripHttp,
} from "./helpers";

interface Props {
  validation: {
    [key in string]: string | undefined;
  };
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  setLinksValidation: Dispatch<
    SetStateAction<{
      [key in string]: string | undefined;
    }>
  >;
  links: SocialMediaLinks[];
  setLinks: Dispatch<SetStateAction<SocialMediaLinks[]>>;
}

const AboutFormLinks = ({
  validation,
  onboardingForm,
  setOnboardingForm,
  setLinksValidation,
  links,
  setLinks,
}: Props) => {
  const { setAlert } = useContext(AlertContext);
  const allKeys = Object.values(SocialMediaFields)?.map((value) => value.key);
  const hasSocialLink = hasSocialMediaProfile(onboardingForm);

  useEffect(() => {
    if (links?.length === 0 && hasSocialLink) {
      const links = convertOnboardingFormToLink(onboardingForm);
      setLinks(links);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSocialLink]);

  const handleChangeField = (
    e: CustomEvent,
    currentFieldName: SocialMediaFieldKey,
  ) => {
    setLinks((prev) => {
      const copy = makeDeepCopy(prev);
      const index = copy.findIndex(
        (link: SocialMediaLinks) => link.fieldName === currentFieldName,
      );
      if (index > -1) {
        copy[index].fieldName = e.target.value as SocialMediaFieldKey;
        copy[index].link = "";
        handleSave(currentFieldName, "");
      }
      return copy;
    });
  };

  const handleAddLink = () => {
    const remaining = getRemainingLinks();
    if (remaining?.length > 0) {
      setLinks((prev) => {
        const copy = makeDeepCopy(prev);
        copy.push({
          fieldName: remaining[0] as SocialMediaFieldKey,
          link: "",
        });
        return copy;
      });
    }
  };

  const handleDeleteLink = (fieldName: SocialMediaFieldKey) => {
    if (links?.length <= 1) {
      setAlert("You must input at least one link", "warning");
      return;
    }
    setLinks((prev) => {
      const copy = makeDeepCopy(prev);
      const index = copy.findIndex(
        (link: SocialMediaLinks) => link.fieldName === fieldName,
      );
      if (index > -1) {
        copy.splice(index, 1);
      }
      handleSave(fieldName, "");
      return copy;
    });
  };

  const getRemainingLinks = () => {
    const existing = links?.map((link) => link.fieldName);
    const remaining = allKeys.filter(
      (item) => !existing?.includes(item as SocialMediaFieldKey),
    );
    return remaining;
  };

  const handleSave = (fieldName: string, value: string) => {
    setOnboardingForm((prev) => ({
      ...prev,
      [`${fieldName}Url`]: value,
    }));
  };

  const handleBlurred = (e: CustomEvent) => {
    const { name, value } = e.target;
    const sanitizedInput =
      name !== SocialMediaFieldKey.portfolio
        ? sanitizeInput(value.trim())
        : stripHttp(value.trim());
    if (name !== SocialMediaFieldKey.portfolio) {
      if (sanitizedInput && !isValidUsername(sanitizedInput)) {
        setLinksValidation((prev) => ({
          ...prev,
          [name]:
            "Must contain only letters, numbers, underscores, hyphens or periods",
        }));
      } else {
        setLinksValidation((prev) => ({
          ...prev,
          [name]: undefined,
        }));
      }
    }
  };

  const handleChanged = (e: CustomEvent) => {
    const { name, value } = e.target;
    const sanitizedInput =
      name !== SocialMediaFieldKey.portfolio
        ? sanitizeInput(value.trim())
        : stripHttp(value.trim());
    if (name === SocialMediaFieldKey.portfolio) {
      handleSave(name, `https://${sanitizedInput}`);
    } else {
      handleSave(
        name,
        `${SocialMediaFields[`${name}Url`]?.url}${sanitizedInput}`,
      );
      if (isValidUsername(value)) {
        setLinksValidation((prev) => ({
          ...prev,
          [name]: undefined,
        }));
      }
    }
  };

  const renderOneLink = (fieldName: SocialMediaFieldKey, link: string) => {
    return (
      <Grid
        container
        columnSpacing={2}
        rowGap={1}
        key={fieldName}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} md="auto">
          <Select
            sx={
              !!validation[fieldName]
                ? [styles.selectSocial, { mb: 2.5 }]
                : styles.selectSocial
            }
            value={fieldName}
            onChange={(e) => handleChangeField(e, fieldName)}
          >
            <MenuItem key={fieldName} value={fieldName}>
              {SocialMediaFields[`${fieldName}Url`]?.name}
            </MenuItem>
            {getRemainingLinks()?.map((key) => (
              <MenuItem key={key} value={key}>
                {SocialMediaFields[`${key}Url`]?.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid container item xs={12} md alignItems="center">
          <Grid item xs>
            <StyledTextField
              fullWidth
              placeholder={
                fieldName === SocialMediaFieldKey.portfolio
                  ? "website.com"
                  : "handle"
              }
              name={fieldName}
              defaultValue={link}
              required
              InputProps={{
                sx: { borderRadius: 4 },
                startAdornment: (
                  <InputAdornment
                    sx={styles.linkAdornment}
                    disablePointerEvents
                    position="start"
                  >
                    {SocialMediaFields[`${fieldName}Url`]?.url}
                  </InputAdornment>
                ),
              }}
              onBlur={handleBlurred}
              onChange={handleChanged}
              error={!!validation[fieldName]}
              helperText={validation[fieldName]}
            />
          </Grid>
          <Grid item xs="auto">
            <IconButton
              sx={{
                color: "purple.main",
                fontSize: 14,
              }}
              onClick={() => handleDeleteLink(fieldName)}
            >
              <Box component="i" className="fa-regular fa-trash" />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    );
  };
  return (
    <>
      <Typography gutterBottom>
        Please share your most active social links below
      </Typography>
      {links?.map((link) => renderOneLink(link.fieldName, link.link))}
      {getRemainingLinks()?.length > 0 && (
        <Grid container>
          <Button onClick={handleAddLink} sx={{ mb: 2, color: "purple.main" }}>
            Add Link +
          </Button>
        </Grid>
      )}
    </>
  );
};

export default AboutFormLinks;
